import { AvailabilityStates } from '@coolblue-development/next-availability-state';
import { Collection } from '@features/product/components/Collection/Collection.types';

/**
 * Enriches a product collection with redirect logic based on discriminant matching.
 *
 * This function analyzes product variants (e.g., different colors, storage sizes) and determines
 * which products are viable alternatives to the current product. It calculates matching scores
 * based on shared discriminant values (like color, storage, accessories) and marks products
 * as redirect candidates if they share enough characteristics with the current product.
 *
 * @param collection - The product collection containing discriminants and product variants
 * @param productId - The ID of the current product to compare against
 * @returns An enriched collection with shouldRedirect flags, sorting, and visibility logic
 *
 * @example
 * // For a Purple 128GB phone, products that are also Purple OR 128GB will be marked as
 * // viable redirects (shouldRedirect: true), while products that share no discriminants
 * // will be marked as shouldRedirect: false
 */
export const matchPropertyValues = (collection: Collection, productId?: number): Collection => {
  // Early return if no product context is provided
  if (!productId) {
    return collection;
  }

  // Step 1: Identify which discriminant values the current product belongs to
  // (e.g., if product is "Purple + 128GB", this will contain those two values)
  const currentProductValues = [];

  // Create a mapped version with flattened product IDs for easier lookup
  const mappedCollection = {
    ...collection,
    discriminants: collection.discriminants.map(discriminant => ({
      ...discriminant,
      values: discriminant.values.map(value => {
        const productIds = value.products
          .filter(product => product !== null)
          .map(product => product?.id);
        return {
          ...value,
          productIds: productIds ?? []
        };
      })
    }))
  };

  // Step 2: Build array of discriminant values that contain the current product
  // This identifies which color, storage, etc. the current product has
  for (const discriminant of mappedCollection.discriminants) {
    for (const value of discriminant.values) {
      if (value.productIds?.includes(productId)) {
        currentProductValues.push(value);
      }
    }
  }

  // Step 3: Calculate matching scores for all products
  // Each product gets a score based on how many discriminant values it shares with the current product
  // Score = number of shared discriminant values (e.g., Purple 128GB phone matches Purple 256GB with score 1)
  const matchingPerProductId: { [key: number]: number } = {};

  for (const discriminant of mappedCollection.discriminants) {
    for (const value of discriminant.values) {
      for (const productId of value?.productIds ?? []) {
        // Initialize score for this product if not yet tracked
        if (!matchingPerProductId[productId]) {
          matchingPerProductId[productId] = 0;
        }

        // Increment score if this product shares a discriminant value with the current product
        if (currentProductValues.includes(value)) {
          matchingPerProductId[productId] = matchingPerProductId[productId] + 1;
        }
      }
    }
  }

  // Step 4: Enrich the collection with redirect logic and sorting
  return {
    ...collection,
    discriminants: collection.discriminants.map(discriminant => ({
      ...discriminant,
      values: discriminant.values.map(value => {
        const products = value.products
          .filter(product => product !== null)
          .map(product => ({
            ...product,
            /**
             * Mark as redirect candidate if:
             * 1. Product matches at least (n-1) discriminants, where n = total discriminants
             *    (e.g., for 2 discriminants, must match at least 1)
             * 2. Product is not permanently out of stock
             *
             * This means if current product is Purple 128GB, a Purple 256GB product
             * matches 1 of 2 discriminants and qualifies as a redirect option
             */
            shouldRedirect:
              matchingPerProductId[product.id] >= collection.discriminants.length - 1 &&
              product?.nullableAvailabilityState !== AvailabilityStates.OutOfStock
          }))
          // Sort by matching score: products with more shared discriminants appear first
          .sort((a, b) => matchingPerProductId[b.id] - matchingPerProductId[a.id]);

        // Filter to only products that meet the minimum matching threshold
        const filteredValue = products.filter(
          product => matchingPerProductId[product.id] >= collection.discriminants.length - 1
        );

        return {
          ...value,
          products,
          // Only show this discriminant value if at least one product meets the threshold
          shouldShowValue: filteredValue.length > 0,
          // Set the best-matching product (highest score) as the default
          product: products ? products[0] : null
        };
      })
    }))
  };
};
